import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import ChatNotificationToggle from './ChatNotificationToggle';

const API = window.location.origin;

const GestionNotificacionesAutomaticas = () => {
  const [config, setConfig] = useState({
    nuevo_registro: false,
    formulario_completado: false,
    nueva_clase: false,
    calificacion_cambiada: false,
    inscripcion_clase: false,
    recordatorio_evento: false,
    notificacion_manual: false,
    beca_aprobada: false,
    beca_rechazada: false,
    beca_expira_pronto: false,
    recordatorio_clases_dia: false,
    nuevo_formulario_evaluacion: false,
    notificacion_tareas: false,
    nuevo_pedido_oracion: false
  });
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [triggeringEventos, setTriggeringEventos] = useState(false);
  const [triggerResult, setTriggerResult] = useState(null);

  const notificationTypes = [
    {
      key: 'nuevo_registro',
      title: '🆕 Nuevo Registro de Usuario',
      description: 'Notifica a administradores cuando se registra un nuevo usuario',
      recipients: 'Administradores'
    },
    {
      key: 'formulario_completado',
      title: '📝 Formulario Completado',
      description: 'Notifica al profesor cuando un estudiante completa un formulario',
      recipients: 'Profesor de la clase'
    },
    {
      key: 'nueva_clase',
      title: '🎓 Nueva Clase Creada',
      description: 'Notifica a administradores cuando se crea una nueva clase',
      recipients: 'Administradores'
    },
    {
      key: 'calificacion_cambiada',
      title: '📊 Calificación Actualizada',
      description: 'Notifica al estudiante cuando se actualiza una calificación',
      recipients: 'Estudiante'
    },
    {
      key: 'inscripcion_clase',
      title: '👥 Inscripción a Clase',
      description: 'Notifica al profesor cuando alguien se inscribe en su clase',
      recipients: 'Profesor de la clase'
    },
    {
      key: 'recordatorio_evento',
      title: '📅 Recordatorio de Evento',
      description: 'Recordatorio automático 1 día antes de cada evento',
      recipients: 'Todos los usuarios'
    },
    {
      key: 'notificacion_manual',
      title: '📨 Notificaciones Manuales',
      description: 'Notificaciones enviadas manualmente por admin/profesores',
      recipients: 'Destinatarios seleccionados'
    },
    {
      key: 'beca_aprobada',
      title: '🎓 Beca Aprobada',
      description: 'Notifica al estudiante cuando su solicitud de beca es aprobada',
      recipients: 'Estudiante solicitante'
    },
    {
      key: 'beca_rechazada',
      title: '❌ Beca Rechazada',
      description: 'Notifica al estudiante cuando su solicitud de beca es rechazada',
      recipients: 'Estudiante solicitante'
    },
    {
      key: 'beca_expira_pronto',
      title: '⏰ Beca Por Vencer',
      description: 'Recordatorio automático 10 días antes de que venza una beca',
      recipients: 'Estudiante becado'
    },
    {
      key: 'recordatorio_clases_dia',
      title: '📅 Clases del Día',
      description: 'Recordatorio diario a las 7:00 AM con las clases programadas para ese día',
      recipients: 'Estudiantes inscritos'
    },
    {
      key: 'nuevo_formulario_evaluacion',
      title: '📝 Nuevo Formulario de Evaluación',
      description: 'Notificación automática cuando se crea un formulario de evaluación en una clase',
      recipients: 'Estudiantes inscritos en la clase'
    },
    {
      key: 'notificacion_tareas',
      title: '✅ Asignación y Actualización de Tareas',
      description: 'Notificación automática cuando se crea o actualiza una tarea asignada a un usuario',
      recipients: 'Usuarios asignados a la tarea'
    },
    {
      key: 'nuevo_pedido_oracion',
      title: '🙏 Nuevo Pedido de Oración',
      description: 'Notifica a todos los usuarios cuando se publica un nuevo pedido de oración en la Red de Oración IBA',
      recipients: 'Todos los usuarios'
    }
  ];

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/admin/notification-types-old`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      
      // Si la respuesta tiene los valores, actualizamos el estado
      if (response.data) {
        setConfig(response.data);
      }
    } catch (error) {
      console.error('Error loading notification config:', error);
      toast.error('Error al cargar configuración de notificaciones');
    } finally {
      setLoading(false);
    }
  };

  const updateConfig = async () => {
    try {
      setUpdating(true);
      await axios.put(`${API}/api/admin/notification-types-old`, config, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      toast.success('Configuración de notificaciones actualizada exitosamente');
    } catch (error) {
      console.error('Error updating notification config:', error);
      toast.error('Error al actualizar configuración');
    } finally {
      setUpdating(false);
    }
  };

  const handleToggle = (key) => {
    setConfig(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const triggerEventosHoy = async () => {
    try {
      setTriggeringEventos(true);
      setTriggerResult(null);
      const response = await axios.post(`${API}/api/notifications/trigger-eventos-hoy`, {}, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      setTriggerResult(response.data);
      if (response.data?.resultado?.eventos_encontrados > 0) {
        toast.success(`Se enviaron notificaciones para ${response.data.resultado.eventos_encontrados} evento(s) de hoy`);
      } else {
        toast.info('No hay eventos programados para hoy');
      }
    } catch (error) {
      console.error('Error triggering eventos:', error);
      toast.error('Error al enviar notificaciones de eventos');
    } finally {
      setTriggeringEventos(false);
    }
  };

  const getActiveCount = () => {
    return Object.values(config).filter(Boolean).length;
  };

  const getTotalCount = () => {
    return Object.keys(config).length;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="text-center">
          <i className="fas fa-spinner fa-spin text-2xl text-blue-600 mb-2"></i>
          <p>Cargando configuración...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestión de Notificaciones Automáticas</h1>
          <p className="text-gray-600">Configura qué eventos del sistema envían notificaciones automáticas por email</p>
        </div>
        <div className="flex items-center space-x-4">
          <Badge variant="secondary" className="text-lg px-3 py-1">
            {getActiveCount()}/{getTotalCount()} Activas
          </Badge>
          <Button 
            onClick={updateConfig}
            disabled={updating}
            className="instituto-gradient text-white"
          >
            {updating ? (
              <>
                <i className="fas fa-spinner fa-spin mr-2"></i>
                Guardando...
              </>
            ) : (
              <>
                <i className="fas fa-save mr-2"></i>
                Guardar Cambios
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <i className="fas fa-bell text-blue-600"></i>
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Notificaciones</p>
                <p className="text-2xl font-bold">{getTotalCount()}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <i className="fas fa-check-circle text-green-600"></i>
              </div>
              <div>
                <p className="text-sm text-gray-600">Activas</p>
                <p className="text-2xl font-bold text-green-600">{getActiveCount()}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <i className="fas fa-pause-circle text-gray-600"></i>
              </div>
              <div>
                <p className="text-sm text-gray-600">Desactivadas</p>
                <p className="text-2xl font-bold text-gray-600">{getTotalCount() - getActiveCount()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions - Manual Trigger */}
      <Card className="border-2 border-amber-200 bg-amber-50" data-testid="eventos-hoy-trigger-card">
        <CardHeader>
          <CardTitle className="flex items-center">
            <i className="fas fa-bolt mr-3 text-amber-600"></i>
            Enviar Notificaciones de Eventos de Hoy
          </CardTitle>
          <CardDescription>
            Enviar push y email a todos los usuarios sobre los eventos programados para hoy. 
            Usa esto si las notificaciones automáticas (10:00 AM) no se ejecutaron.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <Button 
              onClick={triggerEventosHoy}
              disabled={triggeringEventos}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="trigger-eventos-hoy-btn"
            >
              {triggeringEventos ? (
                <>
                  <i className="fas fa-spinner fa-spin mr-2"></i>
                  Enviando...
                </>
              ) : (
                <>
                  <i className="fas fa-paper-plane mr-2"></i>
                  Enviar Ahora
                </>
              )}
            </Button>
            {triggerResult && (
              <div className="text-sm" data-testid="trigger-result">
                {triggerResult.resultado?.eventos_encontrados > 0 ? (
                  <span className="text-green-700 font-medium">
                    <i className="fas fa-check-circle mr-1"></i>
                    {triggerResult.resultado.eventos_encontrados} evento(s) notificado(s)
                  </span>
                ) : (
                  <span className="text-gray-600">
                    <i className="fas fa-info-circle mr-1"></i>
                    No hay eventos para hoy
                  </span>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Chat Notifications Section - Personal Setting */}
      <Card className="border-2 border-blue-200 bg-blue-50">
        <CardHeader>
          <CardTitle className="flex items-center">
            <i className="fas fa-comment-dots mr-3 text-blue-600"></i>
            💬 Notificaciones de Chat (Configuración Personal)
          </CardTitle>
          <CardDescription>
            Activa o desactiva las notificaciones push cuando recibes mensajes en el Chat Institucional
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ChatNotificationToggle />
        </CardContent>
      </Card>

      {/* Notification Types */}
      <div className="space-y-4">
        {notificationTypes.map((type) => (
          <Card key={type.key} className={`transition-all duration-200 ${config[type.key] ? 'ring-2 ring-blue-200 bg-blue-50' : 'bg-white'}`}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">{type.title}</h3>
                    <Badge 
                      variant={config[type.key] ? "default" : "secondary"}
                      className={config[type.key] ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-600"}
                    >
                      {config[type.key] ? 'Activa' : 'Desactivada'}
                    </Badge>
                  </div>
                  
                  <p className="text-gray-600 mb-2">{type.description}</p>
                  
                  <div className="flex items-center space-x-2 text-sm">
                    <i className="fas fa-users text-gray-400"></i>
                    <span className="text-gray-500">Destinatarios: <strong>{type.recipients}</strong></span>
                  </div>
                </div>

                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <Button
                      variant={config[type.key] ? "default" : "outline"}
                      size="sm"
                      onClick={() => handleToggle(type.key)}
                    >
                      <i className={`fas fa-${config[type.key] ? 'check' : 'times'} mr-2`}></i>
                      {config[type.key] ? 'Activada' : 'Desactivada'}
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Footer Info */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-start space-x-3">
            <i className="fas fa-info-circle text-blue-600 mt-1"></i>
            <div className="text-sm text-blue-800">
              <p className="font-semibold mb-1">Información importante:</p>
              <ul className="space-y-1 text-blue-700">
                <li>• Las notificaciones se envían automáticamente cuando ocurren los eventos configurados</li>
                <li>• Cada usuario puede desactivar individualmente las notificaciones desde sus preferencias</li>
                <li>• Los cambios se aplican inmediatamente después de guardar</li>
                <li>• Todas las notificaciones incluyen una copia al email institucional para integración con Zapier</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default GestionNotificacionesAutomaticas;